import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AuthApiError, emailAuth } from '@/api/auth/emailAuth';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/locale';

import { AuthLayout } from './AuthLayout';
import {
  AuthField,
  AuthSwitch,
  FormAlert,
  PasswordField,
  PasswordRequirements,
  SubmitButton,
} from './fields';
import { focusField, friendlyAuthError } from './form';
import { meetsPasswordPolicy } from './password';

export function ForgotScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = useT();

  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; code?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A step swap unmounts the focused submit; land focus on the new step's
  // first field instead of the page body (not on first render).
  const shownStep = useRef(step);
  useEffect(() => {
    if (shownStep.current === step) return;
    shownStep.current = step;
    focusField(step === 'reset' ? 'code' : 'email');
  }, [step]);

  async function onRequest(event: FormEvent) {
    event.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setErrors({ email: t('signup.emailValid') });
      focusField('email');
      return;
    }
    setErrors({});
    setFormError(null);
    setBusy(true);
    try {
      await emailAuth.forgotPassword(email.trim());
      setStep('reset');
    } catch (error) {
      if (error instanceof AuthApiError && error.status === 0) {
        setFormError(t('login.error.network'));
        return;
      }
      setFormError(friendlyAuthError(error, t, 'login.error.unknown'));
    } finally {
      setBusy(false);
    }
  }

  async function onReset(event: FormEvent) {
    event.preventDefault();
    const nextErrors: typeof errors = {};
    if (!/^\d{6}$/.test(code.trim())) nextErrors.code = t('confirm.enterCode');
    if (!meetsPasswordPolicy(password)) nextErrors.password = t('signup.weakPassword');
    setErrors(nextErrors);
    setFormError(null);
    if (Object.keys(nextErrors).length > 0) {
      focusField(nextErrors.code ? 'code' : 'password');
      return;
    }

    setBusy(true);
    try {
      await emailAuth.confirmForgotPassword(email.trim(), code.trim(), password);
      toast.success(t('forgot.success'));
      navigate(`/login?email=${encodeURIComponent(email.trim())}`);
    } catch (error) {
      if (error instanceof AuthApiError && error.status === 0) {
        setFormError(t('login.error.network'));
        return;
      }
      if (error instanceof AuthApiError && /password did not conform|invalidpassword|password.*policy/i.test(error.message)) {
        setErrors({ password: t('signup.weakPassword') });
        return;
      }
      setErrors({ code: friendlyAuthError(error, t, 'forgot.resetFail') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t('forgot.title')}
      lede={
        step === 'request'
          ? t('forgot.lede')
          : t('confirm.codeSent', { email: email.trim() })
      }
    >
      <p className="text-kicker text-ink-secondary">
        {t('forgot.stepOf', { step: step === 'request' ? 1 : 2 })}
      </p>
      {step === 'request' ? (
        <form onSubmit={onRequest} noValidate className="mt-6 flex flex-col gap-5">
          {formError ? <FormAlert>{formError}</FormAlert> : null}
          <AuthField
            id="email"
            label={t('login.email')}
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            value={email}
            error={errors.email}
            disabled={busy}
            onChange={(event) => {
              setEmail(event.target.value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
          />
          <SubmitButton busy={busy}>{t('forgot.sendCode')}</SubmitButton>
        </form>
      ) : (
        <form onSubmit={onReset} noValidate className="mt-6 flex flex-col gap-5">
          {formError ? <FormAlert>{formError}</FormAlert> : null}
          <AuthField
            id="code"
            label={t('forgot.resetCode')}
            className="h-14 text-center text-timecode text-[1.5rem] tracking-[0.4em]"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            error={errors.code}
            disabled={busy}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
              setErrors((prev) => ({ ...prev, code: undefined }));
            }}
          />
          <PasswordField
            id="password"
            label={t('forgot.newPassword')}
            autoComplete="new-password"
            value={password}
            error={errors.password}
            disabled={busy}
            onChange={(event) => {
              setPassword(event.target.value);
              setErrors((prev) => ({ ...prev, password: undefined }));
            }}
            hint={<PasswordRequirements value={password} />}
          />
          <SubmitButton busy={busy}>{t('forgot.setNewPassword')}</SubmitButton>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="self-start"
            disabled={busy}
            onClick={() => setStep('request')}
          >
            {t('common.back')}
          </Button>
        </form>
      )}

      <div className="mt-8">
        <AuthSwitch
          prompt={t('forgot.remembered')}
          to={email.trim() ? `/login?email=${encodeURIComponent(email.trim())}` : '/login'}
          action={t('login.signIn')}
        />
      </div>
    </AuthLayout>
  );
}
